import React, { useState, useEffect } from 'react';
import { Button, Alert, Typography, Box, Link, Card, CardContent } from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material'; // Import the eye icons
import { useNavigate, useLocation } from 'react-router-dom';
import { hosturl } from '../libs/Constants';
import './style.css';
import Colors from './libs/Colors';

const SignIn = () => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [contact, setContact] = useState(''); // For capturing contact input
    const [password, setPassword] = useState(''); // For capturing password input
    const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
    const navigate = useNavigate();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const redirectUrl = query.get('redirect') || localStorage.getItem('redirectUrl');
    const tokenFromUrl = query.get('token');

    useEffect(() => {
        if (tokenFromUrl) {
            localStorage.setItem('token', tokenFromUrl);
            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                navigate('/home');
            }
        }
    }, [tokenFromUrl, redirectUrl, navigate]);

    const onFinish = async (event) => {
        event.preventDefault(); // Prevent default form submission behavior

        setLoading(true);
        setError(null);

        // Sending the form data to the backend
        try {
            const response = await fetch(`${hosturl}/signin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ contact, password }),
            });

            const result = await response.json();

            if (response.ok) {
                localStorage.setItem('token', result.token);
                localStorage.setItem('user', JSON.stringify(result.user)); // Store user details

                localStorage.removeItem('redirectUrl');

                // Redirect after successful login
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                } else {
                    navigate('/home');
                }
            } else {
                setError(result.message || 'An error occurred. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: Colors.primaryColor,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundImage: 'url(https://via.placeholder.com/1920x1080)', // Placeholder background image
            overflow: 'hidden',
        }}>
            <Card sx={{
                width: 400,
                borderRadius: 12,
                boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
                padding: '30px 20px',
                textAlign: 'center',
                backgroundColor: '#fff',
                transform: 'translateY(40px)',
                opacity: 0,
                animation: 'fadeInUp 1s forwards',
            }}>
                <CardContent>
                    <Typography variant="h4" sx={{
                        marginBottom: 3,
                        fontWeight: 'bold',
                        fontSize: '28px',
                        letterSpacing: '0.5px',
                    }}>Sign In</Typography>
                    {error && <Alert severity="error" sx={{
                        marginBottom: 3,
                        fontSize: '14px',
                    }}>{error}</Alert>}

                    <form onSubmit={onFinish} style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            marginBottom: '16px',
                            textAlign: 'left',
                        }}>
                            <label htmlFor="contact" style={{
                                display: 'block',
                                fontSize: '14px',
                                marginBottom: '8px',
                            }}>Email or Phone</label>
                            <div style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '8px',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #ccc',
                                padding: '8px',
                            }}>
                                <Email sx={{
                                    color: '#2E8B57',
                                    marginRight: '8px',
                                }} />
                                <input
                                    id="contact"
                                    name="contact"
                                    type="text"
                                    placeholder="Email or Phone"
                                    style={{
                                        width: '100%',
                                        height: '36px',
                                        padding: '0 8px',
                                        border: 'none',
                                        outline: 'none',
                                        borderRadius: '4px',
                                        backgroundColor: '#f5f5f5',
                                        fontSize: '14px',
                                        color: '#333',
                                    }}
                                    value={contact}
                                    onChange={(e) => setContact(e.target.value)}
                                />
                            </div>
                        </div>
                        <div style={{
                            marginBottom: '16px',
                            textAlign: 'left',
                        }}>
                            <label htmlFor="password" style={{
                                display: 'block',
                                fontSize: '14px',
                                marginBottom: '8px',
                            }}>Password</label>
                            <div style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '8px',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #ccc',
                                padding: '8px',
                            }}>
                                <Lock sx={{
                                    color: '#2E8B57',
                                    marginRight: '8px',
                                }} />
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Password"
                                    style={{
                                        width: '100%',
                                        height: '36px',
                                        padding: '0 8px',
                                        border: 'none',
                                        outline: 'none',
                                        borderRadius: '4px',
                                        backgroundColor: '#f5f5f5',
                                        fontSize: '14px',
                                        color: '#333',
                                    }}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <div onClick={() => setShowPassword(!showPassword)} style={{
                                    cursor: 'pointer',
                                    position: 'absolute',
                                    right: '8px',
                                    color: '#2E8B57',
                                }}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </div>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{
                                background: Colors.primaryColor1,
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: 16,
                                marginTop: 2,
                                padding: '12px 0',
                                borderRadius: '8px',
                                boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)',
                                transition: 'background 0.3s ease, box-shadow 0.3s ease',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #4CAF50, #2E8B57)',
                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                                },
                                '&:active': {
                                    background: '#4CAF50',
                                },
                            }}
                            disabled={loading}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </Button>
                    </form>

                    <Box sx={{
                        marginTop: 3,
                    }}>
                        <Link href="/signup" sx={{
                            display: 'block',
                            color: '#2E8B57',
                            textDecoration: 'none',
                            marginTop: 1,
                            fontWeight: 'bold',
                            fontSize: '14px',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: '#4CAF50',
                            },
                        }}>
                            Don’t have an account? Register here
                        </Link>
                        <Link href="/forgetpassword" sx={{
                            display: 'block',
                            color: '#2E8B57',
                            textDecoration: 'none',
                            marginTop: 1,
                            fontWeight: 'bold',
                            fontSize: '14px',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: '#4CAF50',
                            },
                        }}>
                            Forgot Password?
                        </Link>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SignIn;
